import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Box,
  Typography,
  IconButton,
  Badge,
  CircularProgress,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { Close, Terminal, Refresh, Error } from '@mui/icons-material';
import apiClient from '../utils/apiClient';
import {getAmsterdamOffset}  from '../utils/localisation';
import { useMobile } from '../hooks/useMobile';

const ConsoleDialog = ({
  open,
  onClose,
  title = 'Console Output',
  catanaErrorApiEndpoint = '/error-log-catana',
}) => {
  const [tabValue, setTabValue] = useState(1);
  const theme = useTheme();
  const { isMobile } = useMobile();
  const [dialogHeight, setDialogHeight] = useState('90vh');

  // Catana error log data state
  const [catanaErrorData, setCatanaErrorData] = useState([]);
  const [catanaErrorLoading, setCatanaErrorLoading] = useState(false);
  const [catanaErrorError, setCatanaErrorError] = useState(null);
  const [catanaErrorLastId, setCatanaErrorLastId] = useState(null);

  // Catana conversion data state
  const [conversionData, setConversionData] = useState([]);
  const [conversionLoading, setConversionLoading] = useState(false);
  const [conversionError, setConversionError] = useState(null);

  // Common state
  const [isAutoScroll, setIsAutoScroll] = useState(true);

  // Refs
  const catanaErrorRef = useRef(null);
  const conversionRef = useRef(null);
  const catanaErrorEndRef = useRef(null);
  const conversionEndRef = useRef(null);

  // Update dialog height based on window size
  useEffect(() => {
    const updateHeight = () => {
      const maxHeight = Math.min(window.innerHeight * 0.9, 900);
      setDialogHeight(`${maxHeight}px`);
    };
    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  // Get current tab refs
  const getCurrentRefs = useCallback(() => {
    if (tabValue === 0) {
      return { containerRef: catanaErrorRef, endRef: catanaErrorEndRef };
    } else {
      return { containerRef: conversionRef, endRef: conversionEndRef };
    }
  }, [tabValue]);

  // Check if user is near the bottom of the container
  const checkIfNearBottom = useCallback((containerRef) => {
    if (!containerRef?.current) return true;
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    const threshold = 50;
    return scrollHeight - scrollTop - clientHeight < threshold;
  }, []);

  // Scroll to bottom
  const scrollToBottom = useCallback(() => {
    if (!isAutoScroll) return;
    const { endRef } = getCurrentRefs();
    if (endRef?.current) {
      endRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [isAutoScroll, getCurrentRefs]);

  // Initial fetch of Catana error messages
  const fetchInitialCatanaErrorMessages = useCallback(async () => {
    setCatanaErrorLoading(true);
    setCatanaErrorError(null);
    try {
      const response = await apiClient.get(catanaErrorApiEndpoint, {
        params: { limit: 50 },
      });
      const data = Array.isArray(response.data) ? response.data : [];
      const reversedData = data.reverse();
      setCatanaErrorData(reversedData);
      if (data.length > 0 && data[0].id) {
        setCatanaErrorLastId(Math.max(...data.map((item) => item.id || 0)));
      }
      setTimeout(() => {
        if (catanaErrorEndRef.current) {
          catanaErrorEndRef.current.scrollIntoView({ behavior: 'auto' });
        }
      }, 100);
    } catch (err) {
      console.error('Failed to fetch Catana error data:', err);
      setCatanaErrorError('Failed to load Catana error logs. Please try again.');
    } finally {
      setCatanaErrorLoading(false);
    }
  }, [catanaErrorApiEndpoint]);

  // Initial fetch of Catana conversion data
  const fetchCatanaConversions = useCallback(async () => {
    setConversionLoading(true);
    setConversionError(null);
    try {
      const response = await apiClient.get('/catana-conversion');
      const data = Array.isArray(response.data) ? response.data : [];
      setConversionData(data);
    } catch (err) {
      console.error('Failed to fetch Catana conversion data:', err);
      setConversionError('Failed to load Catana conversion data. Please try again.');
    } finally {
      setConversionLoading(false);
    }
  }, []);

  // Poll for new Catana error messages
  const pollNewCatanaErrorMessages = useCallback(async () => {
    if (!catanaErrorLastId) return;
    try {
      const response = await apiClient.get(catanaErrorApiEndpoint, {
        params: { afterId: catanaErrorLastId },
      });
      const newData = Array.isArray(response.data) ? response.data : [];
      if (newData.length > 0) {
        const wasNearBottom = checkIfNearBottom(catanaErrorRef);
        const reversedNewData = newData.reverse();
        setCatanaErrorData((prev) => {
          const updatedData = [...prev];
          reversedNewData.forEach((newItem) => {
            if (!newItem.id) return;
            const existingIndex = updatedData.findIndex((item) => item.id === newItem.id);
            if (existingIndex !== -1) {
              const newStamp = new Date(newItem.stamp).getTime();
              const existingStamp = new Date(updatedData[existingIndex].stamp).getTime();
              if (newStamp > existingStamp) {
                updatedData[existingIndex] = newItem;
              }
            } else if (newItem.id > catanaErrorLastId) {
              updatedData.push(newItem);
            }
          });
          return updatedData;
        });
        setCatanaErrorLastId(Math.max(...newData.map((item) => item.id || 0)));
        if (wasNearBottom || isAutoScroll) {
          setTimeout(() => {
            if (catanaErrorEndRef.current) {
              catanaErrorEndRef.current.scrollIntoView({ behavior: 'smooth' });
            }
          }, 50);
        }
      }
    } catch (err) {
      console.error('Failed to poll new Catana error messages:', err);
    }
  }, [catanaErrorLastId, catanaErrorApiEndpoint, checkIfNearBottom, isAutoScroll]);

  // Poll for new conversion data
  const pollCatanaConversions = useCallback(async () => {
    try {
      const response = await apiClient.get('/catana-conversion');
      const newData = Array.isArray(response.data) ? response.data : [];
      setConversionData(newData);
    } catch (err) {
      console.error('Failed to poll Catana conversion data:', err);
    }
  }, []);

  // Reset all data when dialog closes
  const resetAllData = useCallback(() => {
    setCatanaErrorData([]);
    setCatanaErrorLastId(null);
    setCatanaErrorError(null);
    setConversionData([]);
    setConversionError(null);
  }, []);

  // Initial fetch when dialog opens
  useEffect(() => {
    if (!open) {
      resetAllData();
      return;
    }
    fetchInitialCatanaErrorMessages();
    fetchCatanaConversions();
  }, [open, fetchInitialCatanaErrorMessages, fetchCatanaConversions, resetAllData]);

  // Set up polling interval - only poll for active tab
  useEffect(() => {
    if (!open) return;
    const interval = setInterval(() => {
      if (tabValue === 0) {
        pollNewCatanaErrorMessages();
      } else {
        pollCatanaConversions();
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [open, tabValue, pollNewCatanaErrorMessages, pollCatanaConversions]);

  // Set overflow for active tab containers
  useEffect(() => {
    if (catanaErrorRef.current) {
      catanaErrorRef.current.style.overflow = 'auto';
    }
    if (conversionRef.current) {
      conversionRef.current.style.overflow = 'auto';
    }
  }, [catanaErrorData, conversionData]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Refresh current tab
  const refreshCurrentTab = () => {
    if (tabValue === 0) {
      fetchInitialCatanaErrorMessages();
    } else {
      fetchCatanaConversions();
    }
  };

  // Format timestamp - Convert UTC to Dutch time and format
  const formatTimestamp = (timestamp) => {
    try {
      const date = new Date(timestamp);
      date.setHours(date.getHours() + getAmsterdamOffset());
      return date.toLocaleTimeString('nl-NL', {
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return 'Invalid Time';
    }
  };

  // Format timestamp with date
  const formatDateTimestamp = (timestamp) => {
    try {
      const date = new Date(timestamp);
      date.setHours(date.getHours() + getAmsterdamOffset());
      return date.toLocaleString('nl-NL', {
        hour12: false,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return 'N/A';
    }
  };

  // Get error count for badge
  const getErrorCount = (data) => {
    const today = new Date();
    return data.filter((item) => {
      const errorDate = new Date(item.stamp);
      return (
        errorDate.getUTCFullYear() === today.getUTCFullYear() &&
        errorDate.getUTCMonth() === today.getUTCMonth() &&
        errorDate.getUTCDate() === today.getUTCDate()
      );
    }).length;
  };

  // Aggregate conversion data by organization
  const aggregateConversions = () => {
    const aggregated = {};
    let totalConversions = 0;

    conversionData.forEach((item) => {
      const { organization_name = 'Unknown', ios_portal_name = 'Unknown', converted = 0, last_conversion } = item;
      if (!aggregated[organization_name]) {
        aggregated[organization_name] = { total: 0, portals: [] };
      }
      aggregated[organization_name].total += Number(converted) || 0;
      aggregated[organization_name].portals.push({
        ios_portal_name,
        converted: Number(converted) || 0,
        last_conversion,
      });
      totalConversions += Number(converted) || 0;
    });

    const sortedAggregated = Object.keys(aggregated)
      .sort()
      .reduce((obj, key) => {
        obj[key] = aggregated[key];
        return obj;
      }, {});

    return { aggregated: sortedAggregated, totalConversions };
  };

  const { aggregated, totalConversions } = aggregateConversions();

  const scrollbarSx = {
    '&::-webkit-scrollbar': { width: '12px' },
    '&::-webkit-scrollbar-track': { backgroundColor: '#000000', borderRadius: '6px' },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#444444',
      borderRadius: '6px',
      border: '2px solid #000000',
      '&:hover': { backgroundColor: '#555555' },
    },
    '&::-webkit-scrollbar-corner': { backgroundColor: '#000000' },
    scrollbarWidth: 'thin',
    scrollbarColor: '#444444 #000000',
  };

  const baseBoxSx = {
    height: '100%',
    backgroundColor: '#1e1e1e',
    color: '#ffffff',
    padding: '10px 2px 2px',
    overflow: 'auto',
    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
    fontSize: '14px',
    lineHeight: 1.4,
    border: '1px solid #333',
    borderRadius: 1,
    ...scrollbarSx,
  };

  // Render Catana error output
  const renderCatanaErrorOutput = () => {
    return (
      <Box sx={baseBoxSx} ref={catanaErrorRef}>
        {catanaErrorLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress sx={{ color: '#ff6b6b' }} />
            <Typography sx={{ ml: 2, color: '#888' }}>Loading Catana errors...</Typography>
          </Box>
        ) : catanaErrorError ? (
          <Typography sx={{ color: '#ff6b6b', fontStyle: 'italic' }}>
            {catanaErrorError}
          </Typography>
        ) : catanaErrorData.length === 0 ? (
          <Typography sx={{ color: '#888', fontStyle: 'italic' }}>
            No Catana error messages
          </Typography>
        ) : (
          <>
            {catanaErrorData.map((item, index) => (
              <Box
                key={item.id}
                className="catana-error-message"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  marginBottom: 1,
                  padding: 2,
                  borderLeft: `4px solid ${theme.palette.error.main}`,
                  backgroundColor: 'rgba(255, 107, 107, 0.1)',
                  borderRadius: '0 4px 4px 0',
                  '&:hover': { backgroundColor: 'rgba(255, 107, 107, 0.15)' },
                }}
                data-index={index}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 1 }}>
                  <Typography
                    component="span"
                    sx={{ color: '#888', fontSize: '12px', fontWeight: 'bold', marginRight: 2 }}
                  >
                    {formatDateTimestamp(item.stamp)}
                  </Typography>
                  <Typography
                    component="span"
                    sx={{ color: theme.palette.error.main, fontSize: '12px', fontWeight: 'bold', textTransform: 'uppercase' }}
                  >
                    ERROR
                  </Typography>
                </Box>
                <Typography
                  component="div"
                  sx={{ color: '#ffffff', wordBreak: 'break-word', whiteSpace: 'pre-wrap', fontSize: '13px' }}
                >
                  {item.error}
                </Typography>
              </Box>
            ))}
            <div ref={catanaErrorEndRef} />
          </>
        )}
      </Box>
    );
  };

  // Render Catana conversion output
  const renderCatanaConversionOutput = () => {
    return (
      <Box sx={{ ...baseBoxSx, display: 'flex', flexDirection: 'column' }} ref={conversionRef}>
        {conversionLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress sx={{ color: '#69db7c' }} />
            <Typography sx={{ ml: 2, color: '#888' }}>Loading conversion data...</Typography>
          </Box>
        ) : conversionError ? (
          <Typography sx={{ color: '#ff6b6b', fontStyle: 'italic' }}>
            {conversionError}
          </Typography>
        ) : conversionData.length === 0 ? (
          <Typography sx={{ color: '#888', fontStyle: 'italic' }}>
            No conversion data available
          </Typography>
        ) : (
          <>
            <TableContainer component={Paper} sx={{ backgroundColor: '#1e1e1e', border: '1px solid #333', flex: 1, overflow: 'auto', maxWidth: '95%' }}>
              <Table sx={{ minWidth: 350, padding: '0 8px' }} size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: '#ffffff', fontWeight: 'bold', borderBottom: '1px solid #333', backgroundColor: '#1e1e1e', position: 'sticky', top: 0, zIndex: 2, width: '20%', maxWidth: '150px', textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                      Organization
                    </TableCell>
                    <TableCell sx={{ color: '#ffffff', fontWeight: 'bold', borderBottom: '1px solid #333', backgroundColor: '#1e1e1e', position: 'sticky', top: 0, zIndex: 2, width: '25%', maxWidth: '180px', textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                      Portal
                    </TableCell>
                    <TableCell sx={{ color: '#ffffff', fontWeight: 'bold', borderBottom: '1px solid #333', backgroundColor: '#1e1e1e', position: 'sticky', top: 0, zIndex: 2, textAlign: 'right', width: '25%' }}>
                      Conversions
                    </TableCell>
                    <TableCell sx={{ color: '#ffffff', fontWeight: 'bold', borderBottom: '1px solid #333', backgroundColor: '#1e1e1e', position: 'sticky', top: 0, zIndex: 2, textAlign: 'right', width: '30%' }}>
                      Last Conversion
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(aggregated).map(([orgName, { total, portals }]) => (
                    <React.Fragment key={orgName}>
                      {portals.map((item, index) => (
                        <TableRow key={`${orgName}-${item.ios_portal_name}`} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell sx={{ color: '#74b9ff', fontWeight: 'bold', fontSize: '16px', borderBottom: '1px solid #333', width: '20%', maxWidth: '150px', textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                            {index === 0 ? orgName : ''}
                          </TableCell>
                          <TableCell sx={{ color: '#ffffff', borderBottom: '1px solid #333', width: '25%', maxWidth: '180px', textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                            {item.ios_portal_name}
                          </TableCell>
                          <TableCell sx={{ color: '#ffffff', borderBottom: '1px solid #333', textAlign: 'right', width: '25%' }}>
                            {item.converted}
                          </TableCell>
                          <TableCell sx={{ color: '#ffffff', borderBottom: '1px solid #333', textAlign: 'right', width: '30%' }}>
                            {item.last_conversion ? formatDateTimestamp(item.last_conversion) : 'N/A'}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell sx={{ borderBottom: '1px solid #333', width: '20%' }}>{''}</TableCell>
                        <TableCell sx={{ borderBottom: '1px solid #333', width: '25%' }} />
                        <TableCell sx={{ color: '#74b9ff', fontWeight: 'bold', fontSize: '16px', borderBottom: '1px solid #333', borderTop: '2px double #555', textAlign: 'right', width: '25%' }}>
                          {total}
                        </TableCell>
                        <TableCell sx={{ borderBottom: '1px solid #333', width: '30%' }} />
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TableContainer component={Paper} sx={{ backgroundColor: '#1e1e1e', border: '1px solid #333', maxWidth: '95%' }}>
              <Table sx={{ minWidth: 350, padding: '0 8px' }} size="small">
                <TableBody>
                  <TableRow sx={{ backgroundColor: '#1e1e1e', position: 'sticky', bottom: 0, zIndex: 2, borderTop: '2px double #555' }}>
                    <TableCell sx={{ borderBottom: '1px solid #333', width: '20%' }}>{''}</TableCell>
                    <TableCell sx={{ borderBottom: '1px solid #333', width: '25%' }} />
                    <TableCell sx={{ color: '#ffffff', fontWeight: 'bold', fontSize: '18px', borderBottom: '1px solid #333', textAlign: 'right', width: '25%' }}>
                      Total: {totalConversions}
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid #333', width: '30%' }} />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            <div ref={conversionEndRef} />
          </>
        )}
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          maxHeight: isMobile ? '100%' : dialogHeight,
          height: isMobile ? '100%' : dialogHeight,
          backgroundColor: '#2d2d2d',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: isMobile ? 0 : undefined,
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: '#1e1e1e',
          color: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          borderBottom: '1px solid #333',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Terminal sx={{ color: '#69db7c' }} />
          <Typography variant="h6">{title}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            onClick={refreshCurrentTab}
            sx={{ color: '#ffffff' }}
            title="Refresh Current Tab"
            disabled={tabValue === 0 ? catanaErrorLoading : conversionLoading}
          >
            <Refresh />
          </IconButton>
          <IconButton onClick={onClose} sx={{ color: '#ffffff' }}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <Box sx={{ backgroundColor: '#1e1e1e', borderBottom: '1px solid #333' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          sx={{
            backgroundColor: '#1e1e1e',
            '& .MuiTab-root': { color: '#ffffff', textTransform: 'none', fontWeight: 500 },
            '& .MuiTabs-indicator': { backgroundColor: '#69db7c' },
          }}
        >
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Error sx={{ fontSize: '18px' }} />
                Catana Errors
                <Badge
                  badgeContent={getErrorCount(catanaErrorData)}
                  color="error"
                  sx={{ '& .MuiBadge-badge': { backgroundColor: '#ff6b6b', color: '#fff' } }}
                />
              </Box>
            }
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Terminal sx={{ fontSize: '18px' }} />
                Catana Conversions
              </Box>
            }
          />
        </Tabs>
      </Box>

      <DialogContent
        sx={{
          backgroundColor: '#2d2d2d',
          padding: 2,
          flex: 1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          {tabValue === 0 ? renderCatanaErrorOutput() : renderCatanaConversionOutput()}
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: 1,
            padding: 1,
            backgroundColor: '#1e1e1e',
            borderRadius: 1,
            fontSize: '12px',
            color: '#888',
          }}
        >
          <Typography variant="caption">
            Auto-scroll: {isAutoScroll ? 'ON' : 'OFF'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              onClick={() => setIsAutoScroll(!isAutoScroll)}
              sx={{ color: isAutoScroll ? '#69db7c' : '#888', textTransform: 'none', fontSize: '11px' }}
            >
              {isAutoScroll ? 'Disable' : 'Enable'} Auto-scroll
            </Button>
            <Button
              size="small"
              onClick={scrollToBottom}
              sx={{ color: '#69db7c', textTransform: 'none', fontSize: '11px' }}
            >
              Scroll to Bottom
            </Button>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          backgroundColor: '#1e1e1e',
          borderTop: '1px solid #333',
          padding: '8px 16px',
        }}
      >
        <Button
          onClick={onClose}
          sx={{ color: '#ffffff', backgroundColor: '#333', '&:hover': { backgroundColor: '#444' } }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ConsoleDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  catanaErrorApiEndpoint: PropTypes.string,
};

export default ConsoleDialog;
